import {
  DRAWER_WIDTH,
  MINI_DRAWER_WIDTH,
  ThemeMode,
} from "@mantis-theme/config";
import useConfig from "@mantis-theme/hooks/useConfig";
import { Menu, DarkMode, LightMode } from "@mui/icons-material";
import { AppBar, Box, IconButton, Toolbar, Stack, Typography } from "@mui/material";
import { Sidebar, UserMenu } from "@shared/components";
import { PropsWithChildren, useEffect, useState } from "react";
import { getStoredToken } from "@core/utils/auth";
import { jwtDecode } from "jwt-decode";

interface JwtPayload {
  email: string;
  name: string;
  googleId: string;
  iat: number;
  exp: number;
}

const ProtectedLayout = ({ children }: PropsWithChildren) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start with false on mobile
  const [isMobileView, setIsMobileView] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const { mode, onChangeMode } = useConfig();

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded?.name) {
          setUserName(decoded.name);
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1250;
      setIsMobileView(isMobile);
      setSidebarOpen(!isMobile);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      {/* Header AppBar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "unset",
          height: "64px",
          backgroundColor: "background.paper",
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.07)",
          zIndex: 99,
          transition: "margin 0.3s ease, width 0.3s ease",
          width: `calc(100% - ${
            !isMobileView ? (sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH) : 0
          }px)`,
          ml: !isMobileView
            ? `${sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH}px`
            : 0,
        }}
      >
        <Toolbar sx={{ display: "flex", gap: 1 }}>
          {isMobileView && (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              edge="start"
              sx={{ mr: 1 }}
            >
              <Menu />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" alignItems="center" spacing={2}>
            {userName && (
              <Typography 
                variant="subtitle2" 
                color="text.primary"
                fontWeight={600}
                noWrap
                sx={{
                  mr: 1,
                  fontSize: '0.9375rem',
                  letterSpacing: '0.02em'
                }}
              >
                {userName}
              </Typography>
            )}
            <IconButton
              color="primary"
              onClick={() =>
                onChangeMode(
                  mode === ThemeMode.DARK ? ThemeMode.LIGHT : ThemeMode.DARK
                )
              }
              aria-label="toggle color mode"
              size="large"
            >
              {mode === ThemeMode.DARK ? <LightMode /> : <DarkMode />}
            </IconButton>
            <UserMenu />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Render a single Sidebar component, its behavior is controlled by isTemporary prop */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        isMobileView={isMobileView}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          transition: "margin 0.3s ease, width 0.3s ease",
          width: `calc(100% - ${
            !isMobileView ? (sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH) : 0
          }px)`,
          marginLeft: !isMobileView
            ? `${sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH}px`
            : 0,
          marginTop: "64px", // Top margin to account for fixed AppBar
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ProtectedLayout;
